import { useMutation } from '@tanstack/react-query'
import { useShallow } from 'zustand/react/shallow'

import { transformSelection } from '../../../services/document/edit-utilities'
import useAppStore from '../stores/store'
import { getTextAroundCursor } from '../utils/formatting-utils'
import { fetchPlaybookRuleEdits } from '../utils/playbook-utils'

export interface PlaybookRuleEditsParams {
  suggestionId: string
  suggestionText: string
  clauseText: string
  range: Word.Range
  referenceId: string
  ruleName: string
  useOriginalText: boolean
}

export const useFetchPlaybookRuleEdits = () => {
  const { setRangeForReference } = useAppStore(
    useShallow((s) => ({
      setRangeForReference: s.setRangeForReference,
    }))
  )

  return useMutation({
    mutationFn: async ({
      suggestionId,
      suggestionText,
      clauseText,
      range,
      referenceId,
      ruleName,
      useOriginalText,
    }: PlaybookRuleEditsParams) => {
      if (!useOriginalText) {
        const documentContext = await getTextAroundCursor()
        const response = await fetchPlaybookRuleEdits(
          suggestionId,
          suggestionText,
          clauseText,
          documentContext
        )
        await transformSelection(range, response.editedText)
      } else {
        range.context.document.changeTrackingMode = 'Off'
        range.insertText(suggestionText.trimEnd(), Word.InsertLocation.replace)
        await range.context.sync()
        return
      }

      // transformSelection may add text to the paragraph before the tracked range, so we need to reset the tracked range to the entire paragraph.
      const newRange = range.expandTo(
        range.paragraphs.getFirst().getRange(Word.RangeLocation.whole)
      )
      newRange.load('text')
      newRange.select()
      newRange.track()
      await newRange.context.sync()
      setRangeForReference(ruleName, referenceId, newRange)
      range.untrack()
    },
  })
}
